<template>
    <div class="tw:space-y-6">
        <div class="tw:grid tw:grid-cols-12 tw:gap-4">
            <div class="tw:col-span-12 tw:md:col-span-6 tw:lg:col-span-3 tw:min-w-0">
                <StatsCard
                    title="Importe total"
                    :value="formatCurrency(estadisticas?.total_importe)"
                    icon="fas fa-coins"
                    icon-color="tw:text-blue-600"
                    :loading="loading"
                    variant="primary"
                />
            </div>
            <div class="tw:col-span-12 tw:md:col-span-6 tw:lg:col-span-3 tw:min-w-0">
                <StatsCard
                    title="Importe pagado"
                    :value="formatCurrency(estadisticas?.total_importe_pagado)"
                    icon="fas fa-check-circle"
                    icon-color="tw:text-green-500"
                    :loading="loading"
                    variant="success"
                />
            </div>
            <div class="tw:col-span-12 tw:md:col-span-6 tw:lg:col-span-3 tw:min-w-0">
                <StatsCard
                    title="Importe pendiente"
                    :value="formatCurrency(estadisticas?.total_importe_pendiente)"
                    icon="fas fa-clock"
                    icon-color="tw:text-yellow-500"
                    :loading="loading"
                    variant="warning"
                />
            </div>
            <div class="tw:col-span-12 tw:md:col-span-6 tw:lg:col-span-3 tw:min-w-0">
                <StatsCard
                    :title="context === 'abonos' ? 'Abonados con deuda' : 'Bases con deuda'"
                    :value="formatNumber(
                        context === 'abonos'
                            ? estadisticas?.abonados_con_deuda
                            : estadisticas?.bases_con_deuda
                    )"
                    icon="fas fa-user-clock"
                    icon-color="tw:text-red-500"
                    :loading="loading"
                    variant="danger"
                />
            </div>
        </div>

        <div
            class="tw:relative tw:bg-white tw:rounded-xl tw:border tw:border-gray-200 tw:p-4"
            :class="{ 'tw:min-h-[120px]': loading && !estadisticas }"
        >
            <!-- Loading overlay on desglose -->
            <div
                v-if="loading && estadisticas"
                class="tw:absolute tw:inset-0 tw:bg-white/60 tw:z-10 tw:flex tw:items-center tw:justify-center tw:rounded-xl"
            >
                <svg class="tw:animate-spin tw:h-6 tw:w-6 tw:text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="tw:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
            </div>

            <!-- First load spinner -->
            <div
                v-else-if="loading && !estadisticas"
                class="tw:flex tw:items-center tw:justify-center tw:py-8"
            >
                <svg class="tw:animate-spin tw:h-8 tw:w-8 tw:text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="tw:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
            </div>

            <template v-if="estadisticas">
                <h4 class="tw:text-sm tw:font-bold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:mb-3">
                    Desglose por estado
                </h4>
                <div class="tw:grid tw:grid-cols-2 tw:sm:grid-cols-4 tw:lg:grid-cols-7 tw:gap-3">
                    <div
                        class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:p-3 tw:rounded-lg tw:bg-blue-50 tw:border tw:border-blue-100"
                    >
                        <span class="tw:text-xs tw:font-medium tw:text-blue-500 tw:mb-1">
                            Total plazos
                        </span>
                        <span class="tw:text-xl tw:font-bold tw:text-blue-700">
                            {{ formatNumber(estadisticas?.total_plazos) }}
                        </span>
                    </div>
                    <div
                        v-for="item in desglosePorEstado"
                        :key="item.estado"
                        class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:p-3 tw:rounded-lg tw:bg-gray-50"
                    >
                        <div
                            class="tw:h-2.5 tw:w-2.5 tw:rounded-full tw:mb-2"
                            :class="item.dotClass"
                        ></div>
                        <span class="tw:text-xs tw:font-medium tw:text-gray-500 tw:mb-1">
                            {{ item.label }}
                        </span>
                        <span class="tw:text-lg tw:font-bold" :class="item.colorClass">
                            {{ formatNumber(item.count) }}
                        </span>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import StatsCard from '@/modules/admin/ui/StatsCard.vue';

    const props = defineProps({
        estadisticas: {
            type: Object,
            default: () => null
        },
        loading: {
            type: Boolean,
            default: false
        },
        context: {
            type: String,
            default: 'abonos',
            validator: value => ['abonos', 'bases'].includes(value)
        }
    });

    function formatNumber(value) {
        const number = Number(value);
        return Number.isFinite(number) ? number.toLocaleString('es-ES') : '0';
    }

    function formatCurrency(value) {
        const number = Number(value);
        if (!Number.isFinite(number)) {
            return '0,00 €';
        }
        return (
            number.toLocaleString('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }) + ' €'
        );
    }

    const desglosePorEstado = computed(() => {
        const desglose = props.estadisticas?.desglose_por_estado || [];
        const estados = [
            { key: 'pendiente', label: 'Pendiente', colorClass: 'tw:text-yellow-600', dotClass: 'tw:bg-yellow-400' },
            { key: 'pagado', label: 'Pagado', colorClass: 'tw:text-green-600', dotClass: 'tw:bg-green-500' },
            { key: 'anulado', label: 'Anulado', colorClass: 'tw:text-gray-500', dotClass: 'tw:bg-gray-500' },
            { key: 'fallido', label: 'Fallido', colorClass: 'tw:text-red-500', dotClass: 'tw:bg-orange-400' },
            { key: 'fallido_definitivo', label: 'Fallido Def.', colorClass: 'tw:text-red-700', dotClass: 'tw:bg-red-500' },
            { key: 'bloqueado', label: 'Bloqueado', colorClass: 'tw:text-orange-500', dotClass: 'tw:bg-gray-900' }
        ];

        const map = {};
        if (Array.isArray(desglose)) {
            desglose.forEach(item => {
                const estadoKey = item.estado_string?.toLowerCase().replace(/ /g, '_');
                if (estadoKey) {
                    map[estadoKey] = item.count || 0;
                }
            });
        }

        return estados.map(estado => ({
            estado: estado.key,
            label: estado.label,
            count: map[estado.key] ?? 0,
            colorClass: estado.colorClass,
            dotClass: estado.dotClass
        }));
    });
</script>
